/**
 * Utility functions for handling macro replacement in API configurations
 * Macros follow the pattern {path.to.value} and are replaced with values from dataset
 */

/**
 * Validates if a macro path exists in the dataset and returns a valid value
 * @param {string} path - Dot-notation path (e.g., 'body.test', 'headers.Authorization')
 * @param {Array} dataset - Array of objects containing macro values
 * @returns {boolean} - True if path exists and has a valid value
 */
export function validateMacroPath(path, dataset) {
  if (!Array.isArray(dataset) || dataset.length === 0) {
    return false;
  }

  try {
    const pathParts = path.split('.');
    let value = dataset[0];

    for (const part of pathParts) {
      if (value && typeof value === 'object' && part in value) {
        value = value[part];
      } else {
        return false;
      }
    }

    // valid if value exists and is not null/empty/object
    return value != null && value !== '' && typeof value !== 'object';
  } catch (error) {
    return false;
  }
}

/**
 * Extracts value from dataset using dot notation path
 * @param {string} path - Dot-notation path
 * @param {Array} dataset - Array of objects containing values
 * @returns {string|null} - String value or null if not found/invalid
 */
export function extractValueFromDataset(path, dataset) {
  try {
    // Check if dataset is an array and has at least one element
    if (!Array.isArray(dataset) || dataset.length === 0) {
      return null;
    }

    const pathParts = path.split('.');
    let value = dataset[0];

    for (const part of pathParts) {
      if (value && typeof value === 'object' && part in value) {
        value = value[part];
      } else {
        return null;
      }
    }

    return value != null && value !== '' && typeof value !== 'object' ? String(value) : null;
  } catch (error) {
    console.warn('Error extracting value from dataset:', error);
    return null;
  }
}

/**
 * Replaces all macros in a string with values from dataset
 * @param {string} str - String containing macros in {path.to.value} format
 * @param {Array} dataset - Array of objects containing macro values
 * @returns {string} - String with macros replaced
 */
export function replaceMacrosInString(str, dataset) {
  if (typeof str !== 'string' || !Array.isArray(dataset) || dataset.length === 0) {
    return str;
  }

  // Regex matches {word.word.word} pattern but not nested braces or JSON structures
  const macroPattern = /\{([a-zA-Z_][a-zA-Z0-9_.+\-*/]*(?:\.[a-zA-Z_][a-zA-Z0-9_.+\-*/]*)*)\}/g;

  return str.replace(macroPattern, (match, path) => {
    if (validateMacroPath(path, dataset)) {
      const value = extractValueFromDataset(path, dataset);
      return value !== null ? value : match;
    }
    return match; // leave unchanged if path doesn't exist in dataset
  });
}

/**
 * Recursively processes all macros in an object/array structure
 * @param {any} obj - Object, array, or primitive to process
 * @param {Array} dataset - Array of objects containing macro values
 * @returns {any} - Processed object with macros replaced
 */
export function processAllMacros(obj, dataset) {
  if (typeof obj === 'string') {
    return replaceMacrosInString(obj, dataset);
  } else if (Array.isArray(obj)) {
    return obj.map((item) => processAllMacros(item, dataset));
  } else if (typeof obj === 'object' && obj !== null) {
    const result = {};
    for (const [key, value] of Object.entries(obj)) {
      result[key] = processAllMacros(value, dataset);
    }
    return result;
  }
  return obj;
}

/**
 * Extracts dataset from parentDataSetSamples for api_server flows
 * @param {Object} parentDataSet - Parent dataset object
 * @param {Object} parentDataSetSamples - Parent dataset samples object
 * @param {Array} [editedSamples] - Optional edited samples array from user (takes precedence)
 * @returns {Array} - Extracted dataset array or empty array
 */
export function extractDatasetFromApiServerFlow(
  parentDataSet,
  parentDataSetSamples,
  editedSamples = null,
) {
  if (editedSamples && Array.isArray(editedSamples) && editedSamples.length > 0) {
    return editedSamples.reduce((acc, sample) => {
      if (sample && typeof sample === 'object') {
        const rawData = sample.rawMessage || sample;
        if (rawData && typeof rawData === 'object') {
          acc.push(rawData);
        }
      }
      return acc;
    }, []);
  }

  if (!parentDataSet || !parentDataSetSamples) {
    return [];
  }

  const flowType =
    parentDataSet?.['source_config']?.['pipeline.type'] ||
    parentDataSet?.source_config?.flow_type ||
    '';

  // only extract for api_server flows with nested samples
  if (
    flowType === 'api_server' &&
    parentDataSetSamples?.nestedSamples &&
    Array.isArray(parentDataSetSamples.nestedSamples) &&
    parentDataSetSamples.nestedSamples.length > 0
  ) {
    // extract rawMessage from samples, filtering out invalid ones
    return parentDataSetSamples.nestedSamples.reduce((acc, sample) => {
      if (sample && sample.rawMessage && typeof sample.rawMessage === 'object') {
        acc.push(sample.rawMessage);
      }
      return acc;
    }, []);
  }

  return [];
}

/**
 * Generates macro suggestion objects from parent dataset samples for api_server flows
 * Extracts nested keys like {path_params.customerID}, {query_params.active}, etc.
 *
 * @param {Object} parentDataSet - Parent dataset object
 * @param {Object} parentDataSetSamples - Parent dataset samples object
 * @returns {Array} - Array of macro suggestion objects with name, display_name, type, type_display
 *
 * @example
 * // Returns macros like:
 * // [{name: '{path_params.customerID}', display_name: 'path_params.customerID', type: 'attr', type_display: 'Data Attributes'}]
 */
export function extractMacrosFromApiServerFlow(
  parentDataSet,
  parentDataSetSamples,
  editedSamples = null,
) {
  if (!parentDataSet || !parentDataSetSamples) {
    return [];
  }

  // Check if this is an api_server flow
  const flowType =
    parentDataSet?.['source_config']?.['pipeline.type'] ||
    parentDataSet?.source_config?.flow_type ||
    '';

  if (flowType !== 'api_server') {
    return [];
  }

  // Extract dataset using existing utility (with edited samples if provided)
  const dataset = extractDatasetFromApiServerFlow(
    parentDataSet,
    parentDataSetSamples,
    editedSamples,
  );

  if (!Array.isArray(dataset) || dataset.length === 0) {
    return [];
  }

  // Use first sample to extract structure
  const firstSample = dataset[0];
  if (!firstSample || typeof firstSample !== 'object') {
    return [];
  }

  const allMacros = [];

  // Iterate through top-level keys (body, headers, path_params, query_params, etc.)
  Object.keys(firstSample).forEach((topLevelKey) => {
    const value = firstSample[topLevelKey];

    // If the value is an object (not array), extract nested keys
    // This handles structures like {headers: {header1: "value"}} → {headers.header1}
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      Object.keys(value).forEach((nestedKey) => {
        allMacros.push({
          name: `{${topLevelKey}.${nestedKey}}`,
          display_name: `${topLevelKey}.${nestedKey}`,
          type: 'attr',
          type_display: 'Data Attributes',
        });
      });
    } else if (value != null && value !== '' && typeof value !== 'object') {
      // If the value is a primitive (string, number, boolean), create a macro for the top-level key
      // This handles flat structures like {test: "abc"} → {test}
      allMacros.push({
        name: `{${topLevelKey}}`,
        display_name: topLevelKey,
        type: 'attr',
        type_display: 'Data Attributes',
      });
    }
  });

  return allMacros;
}

/**
 * Applies macro replacement to configuration objects
 *
 * @param {Object} config - Configuration object (sourceConfig or sinkConfig)
 * @param {Array|Object} datasetOrParentDataSet - Either:
 *   - Array of objects containing macro values (direct dataset)
 *   - Parent dataset object for api_server flows
 * @param {Object} [parentDataSetSamples] - Parent dataset samples object (for api_server flows)
 * @param {Array} [editedSamples] - Optional edited samples array from user (takes precedence)
 * @returns {Object} - Config with macros replaced or original config if no replacement needed
 *
 * @example
 * // Direct dataset usage
 * applyMacroReplacement(config, [{ id: 123, name: 'test' }])
 *
 * // API server flow usage
 * applyMacroReplacement(config, parentDataSet, parentDataSetSamples)
 *
 * // API server flow usage with edited samples
 * applyMacroReplacement(config, parentDataSet, parentDataSetSamples, editedSamples)
 */
export function applyMacroReplacement(
  config,
  datasetOrParentDataSet,
  parentDataSetSamples,
  editedSamples = null,
) {
  let dataset;

  // Check if this is the api_server flow usage (3+ parameters)
  if (arguments.length >= 3 && parentDataSetSamples) {
    dataset = extractDatasetFromApiServerFlow(
      datasetOrParentDataSet,
      parentDataSetSamples,
      editedSamples,
    );
  }
  // Check if second parameter is already a dataset array
  else if (Array.isArray(datasetOrParentDataSet)) {
    dataset = datasetOrParentDataSet;
  }
  // Handle the case where parentDataSet is passed but no parentDataSetSamples
  else if (datasetOrParentDataSet && typeof datasetOrParentDataSet === 'object') {
    dataset = extractDatasetFromApiServerFlow(datasetOrParentDataSet, parentDataSetSamples);
  } else {
    dataset = [];
  }

  if (!Array.isArray(dataset) || dataset.length === 0) {
    return config;
  }

  // Apply macro replacement to the entire config
  return processAllMacros(config, dataset);
}
